import { useState, useEffect, useCallback, useRef } from 'react';
import { handleApiError } from '../services/api';

// Generic hook for API calls with loading states
export const useApi = (apiCall, dependencies = []) => {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (...args) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await apiCall(...args);
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, dependencies);

  return { ...state, execute };
};

// Hook for API calls with automatic execution on mount
export const useApiEffect = (apiCall, dependencies = []) => {
  const { data, loading, error, execute } = useApi(apiCall, dependencies);

  useEffect(() => {
    // Only execute if we have the required dependencies (e.g., currentInstance.id is truthy)
    const shouldExecute = dependencies.length === 0 || dependencies.every(dep => dep != null);
    if (shouldExecute) {
      execute();
    }
  }, dependencies);

  return { data, loading, error, refetch: execute };
};

// Hook for paginated data
export const usePaginatedApi = (apiCall, dependencies = []) => {
  const [state, setState] = useState({
    data: [],
    loading: false,
    error: null,
    hasMore: true,
    cursor: null,
  });

  // Use ref to track current state and avoid stale closures
  const stateRef = useRef(state);
  const apiCallRef = useRef(apiCall);
  
  // Update refs when state or apiCall changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  const loadMore = useCallback(async (...args) => {
    const currentState = stateRef.current;
    if (currentState.loading || !currentState.hasMore) return;

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await apiCallRef.current(...args, currentState.cursor);
      
      setState(prev => ({
        data: currentState.cursor ? [...prev.data, ...result.posts] : result.posts,
        loading: false,
        error: null,
        hasMore: result.hasMore,
        cursor: result.nextCursor,
      }));
      
      return result;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      throw error;
    }
  }, []); // No dependencies - use refs for current values

  const reset = useCallback(() => {
    setState({
      data: [],
      loading: false,
      error: null,  
      hasMore: true,
      cursor: null,
    });
  }, []);

  return { ...state, loadMore, reset };
};

// Hook for optimistic updates
export const useOptimisticUpdate = (initialData = []) => {
  const [data, setData] = useState(initialData);

  const optimisticUpdate = useCallback((updateFn, revertFn, apiCall) => {
    // Apply optimistic update
    const originalData = data;
    const newData = updateFn(data);
    setData(newData);

    // Execute API call
    apiCall()
      .catch((error) => {
        // Revert on error
        if (revertFn) {
          setData(revertFn(originalData));
        } else {
          setData(originalData);
        }
        throw error;
      });
  }, [data]);

  return { data, setData, optimisticUpdate };
};

// Hook for real-time job updates
export const useJobUpdates = (jobId, onUpdate, onComplete) => {
  const [jobStatus, setJobStatus] = useState(null);
  const [ws, setWs] = useState(null);

  useEffect(() => {
    if (!jobId) return;

    const { jobsAPI } = require('../services/api');
    
    const websocket = jobsAPI.createWebSocket(
      jobId,
      (message) => {
        setJobStatus(message.data);
        if (onUpdate) onUpdate(message.data);
        
        if (message.data.status === 'completed' || message.data.status === 'failed') {
          if (onComplete) onComplete(message.data);
        }
      },
      (error) => {
        console.error('Job WebSocket error:', error);
      }
    );

    setWs(websocket);

    return () => {
      if (websocket) {
        websocket.close();
      }
    };
  }, [jobId, onUpdate, onComplete]);

  const disconnect = useCallback(() => {
    if (ws) {
      ws.close();
      setWs(null);
    }
  }, [ws]);

  return { jobStatus, disconnect };
};

// Hook for polling
export const usePolling = (apiCall, interval = 5000, dependencies = []) => {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null,
  });
  const [isPolling, setIsPolling] = useState(false);

  const execute = useCallback(async (...args) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await apiCall(...args);
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState({ data: null, loading: false, error: errorMessage });
      throw error;
    }
  }, dependencies);

  const startPolling = useCallback((...args) => {
    setIsPolling(true);
    execute(...args);
    
    const intervalId = setInterval(() => {
      execute(...args);
    }, interval);

    return () => {
      clearInterval(intervalId);
      setIsPolling(false);
    };
  }, [execute, interval]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
  }, []);

  return { 
    ...state, 
    isPolling, 
    startPolling, 
    stopPolling, 
    execute 
  };
};